import { CopyId } from "@/components/copy-id";
import { BadgeFilterCanvas } from "@/components/badge-filter-canvas";
import { Badge } from "@/components/ui/badge";
import { TableCell, TableRow } from "@/components/ui/table";
import { type BadgeDto } from "@/providers/badges.provider";
import { IconMedal } from "@tabler/icons-react";

export function VisibilityBadge({ isVisible }: { isVisible: boolean }) {
  return isVisible ? (
    <Badge variant="outline" className="border-green-500 text-green-600">
      Visible
    </Badge>
  ) : (
    <Badge variant="secondary">Hidden</Badge>
  );
}

export function AvailabilityBadge({
  availableFrom,
  availableTo,
}: {
  availableFrom: string | null;
  availableTo: string | null;
}) {
  const now = Date.now();
  const from = availableFrom ? new Date(availableFrom).getTime() : null;
  const to = availableTo ? new Date(availableTo).getTime() : null;

  if (from !== null && now < from) {
    return <Badge variant="secondary">Upcoming</Badge>;
  }
  if (to !== null && now > to) {
    return (
      <Badge variant="outline" className="border-orange-500 text-orange-600">
        Expired
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-blue-500 text-blue-600">
      Active
    </Badge>
  );
}

export function BadgeRow({
  badge,
  onClick,
}: {
  badge: BadgeDto;
  onClick: () => void;
}) {
  const availableFrom = badge.available_from
    ? new Date(badge.available_from).toLocaleDateString()
    : "—";
  const availableTo = badge.available_to
    ? new Date(badge.available_to).toLocaleDateString()
    : "—";

  return (
    <TableRow className="cursor-pointer" onClick={onClick}>
      <TableCell>
        <CopyId id={badge.badge_id} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          {badge.img_url ? (
            badge.filter_img_url ? (
              <div className="size-8 rounded border overflow-hidden relative shrink-0">
                <BadgeFilterCanvas
                  baseImageUrl={badge.img_url}
                  filterImageUrl={badge.filter_img_url}
                  size={32}
                  opacity={0.5}
                />
              </div>
            ) : (
              <img
                src={badge.img_url}
                alt={badge.name}
                className="size-8 rounded object-cover border"
              />
            )
          ) : (
            <div className="size-8 rounded border bg-muted flex items-center justify-center">
              <IconMedal className="size-4 text-muted-foreground" />
            </div>
          )}
          <span className="font-medium">{badge.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <Badge
          variant={badge.variant_name === "standard" ? "secondary" : "outline"}
          className={`text-[10px] px-1.5 py-0 font-mono${badge.variant_name === "shiny" ? " border-yellow-500 text-yellow-600" : ""}`}
        >
          {badge.variant_name}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground text-xs font-mono">
        {badge.action_type}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {badge.action_threshold.toLocaleString()}
      </TableCell>
      <TableCell>
        <AvailabilityBadge
          availableFrom={badge.available_from}
          availableTo={badge.available_to}
        />
      </TableCell>
      <TableCell>
        <VisibilityBadge isVisible={badge.is_visible} />
      </TableCell>
      <TableCell className="text-muted-foreground text-xs">
        {availableFrom} – {availableTo}
      </TableCell>
    </TableRow>
  );
}
